import React from "react";
import PropTypes from 'prop-types';
import { defaultTemplate } from "core/hoc";
import { BaasicButton, BaasicConfirmModal, DatePicker } from 'core/components';
import { Content } from 'core/layouts';
import { observer } from "mobx-react";
import { NikoPatientInsuranceEdit } from "application/niko-patient/components";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { toUTCDate } from "core/utils";

function NikoPatientInsurancePreviewTemplate({ nikoPatientInsurancePreviewViewStore }) {
  const {
    patientInsurance,
    loading,
    disabled,
    showAddPrimaryActive,
    showAddPrimaryInactive,
    showAddSecondaryActive,
    showAddSecondaryInactive,
    showChangePrimaryActive,
    showChangeSecondaryActive,
    showEditPrimaryActive,
    showEditPrimaryInactive,
    showEditSecondaryActive,
    showEditSecondaryInactive,
    showDeletePrimaryActive,
    showDeletePrimaryInactive,
    showDeleteSecondaryActive,
    showDeleteSecondaryInactive,
    showInactivatePrimaryActive,
    showInactivatePrimaryInactive,
    showInactivateSecondaryActive,
    showInactivateSecondaryInactive,
    showDeleteAllowedPrimaryActive,
    showDeleteAllowedPrimaryInactive,
    showDeleteAllowedSecondaryActive,
    showDeleteAllowedSecondaryInactive,
    openAddCard,
    openEditCard,
    openDeleteCard,
    openInactivateCard,
    openChangeCard,
    insuranceChangeConfirmModal
  } = nikoPatientInsurancePreviewViewStore;

  const renderActionButton = (icon, activeIcon, active, onClick) => {
    return (
      <BaasicButton
        className={`btn btn--sml btn--icon btn--icon--square u-align--v--middle${active ? ' is-active' : ''}`}
        icon={`u-icon u-icon--sml ${active ? activeIcon : icon}`}
        label=""
        onClick={onClick}
        disabled={disabled}
      />
    )
  };

  const renderDeleteOrInactivate = (keyPrefix, deleteAllowed, active) => {
    const icon = deleteAllowed
      ? (active ? "u-icon--delete--brand" : "u-icon--delete--primary")
      : (active ? "u-icon--danger--primary" : "u-icon--danger--grey");
    return (
        <BaasicButton
          className={`btn btn--icon btn--icon--square u-align--v--middle${active ? ' is-active' : ''}`}
          icon={`u-icon u-icon--sml ${icon}`}
          label={deleteAllowed ? "Delete" : "Inactivate"}
          onlyIcon={true}
          onClick={() => deleteAllowed ? openDeleteCard(keyPrefix) : openInactivateCard(keyPrefix)}
          disabled={disabled}
        />
    )
  };

  return (
    <div style={{ minHeight: 225 }} className="col-sml-12">
      <Content loading={!patientInsurance || loading}>
        <ReactTooltip positionStrategy="fixed" id="registerTip" place="bottom" />
        <div className="modal__body">
          <div>
            <div className="card--sml card--outlined u-mar--bottom--sml--alt">
              <div className={` ${patientInsurance.primaryActiveId ? 'u-mar--bottom--sml--alt' : 'u-mar--bottom--tny'}`}>
                <h3 className='modal__header__title u-mar--bottom--sml'>Primary Insurance</h3>
                {!patientInsurance.primaryActiveId &&
                  <div className="status--sqare__wrapper w--max--300">

                    <BaasicButton
                      className="btn btn--sml btn--card btn--card--dark"
                      icon="u-icon u-icon--sml u-icon--add-lined u-mar--right--xtny"
                      label="Add Policy"
                      onClick={() => openAddCard("primaryActive")}
                      disabled={disabled}
                      showTooltip={false}
                    />
                  </div>
                }
              </div>
              {/* Add CSS class "table--insurance-plans" to table below to fix the issue of table not being responsive in mobile view. */}
              <table className="table w--100 type--color--primary u-mar--top--tny">
                <tbody>
                  {showAddPrimaryActive &&
                    <InsuranceCard
                      nikoPatientInsurancePreviewViewStore={nikoPatientInsurancePreviewViewStore}
                      keyPrefix="primaryActive"
                      action='Add'
                    />
                  }
                  {patientInsurance.primaryActiveId &&
                    <tr>
                      <td>{patientInsurance.primaryActiveInsurance.name}</td>
                      <td className="type--right">{patientInsurance.primaryActivePolicyNo}</td>
                      <td className="type--right">{toUTCDate(patientInsurance.primaryActiveStartDate)}</td>
                      <td className="type--right">
                        <div className="insurance-action u-flex u-flex--default u-align--h--right u-flex--justify-end">
                          <div className="status--sqare__wrapper">
                            {renderActionButton("u-icon--reload--alt", "u-icon--reload--primary", showChangePrimaryActive, () => openChangeCard("primaryActive"))}
                          </div>
                          {renderActionButton("u-icon--edit-alt", "u-icon--edit-alt--primary", showEditPrimaryActive, () => openEditCard("primaryActive"))}
                          {renderDeleteOrInactivate(
                            "primaryActive",
                            showDeleteAllowedPrimaryActive,
                            showDeletePrimaryActive || showInactivatePrimaryActive
                          )}
                        </div>
                      </td>
                    </tr>
                  }
                  {showEditPrimaryActive &&
                    <EditInsuranceCard
                      nikoPatientInsurancePreviewViewStore={nikoPatientInsurancePreviewViewStore}
                      keyPrefix="primaryActive"
                    />
                  }
                  {showDeletePrimaryActive &&
                    <DeleteInsuranceCard
                      nikoPatientInsurancePreviewViewStore={nikoPatientInsurancePreviewViewStore}
                      keyPrefix="primaryActive"
                    />
                  }
                  {showInactivatePrimaryActive &&
                    <InactivateInsuranceCard
                      nikoPatientInsurancePreviewViewStore={nikoPatientInsurancePreviewViewStore}
                      keyPrefix="primaryActive"
                    />
                  }
                  {showChangePrimaryActive &&
                    <InsuranceCard
                      nikoPatientInsurancePreviewViewStore={nikoPatientInsurancePreviewViewStore}
                      keyPrefix="primaryActive"
                      action="Change"
                      attached
                    />
                  }
                  {(!patientInsurance.primaryActiveId || patientInsurance.primaryActiveInsurance.name !== "Medicare") && patientInsurance.primaryInactiveId &&
                    <tr>
                      <td>
                        <span>Medicare</span>
                        <div className="badge badge--tny badge--primary--light u-mar--left--xsml">Primary inactive</div>
                      </td>
                      <td className="type--right">{patientInsurance.primaryInactivePolicyNo}</td>
                      <td className="type--right">{toUTCDate(patientInsurance.primaryInactiveStartDate)}</td>
                      <td className="type--right">
                        <div className="insurance-action u-flex u-flex--default u-align--h--right u-flex--justify-end">
                          {renderActionButton("u-icon--edit-alt", "u-icon--edit-alt--primary", showEditPrimaryInactive, () => openEditCard("primaryInactive"))}
                          {renderDeleteOrInactivate("primaryInactive", showDeleteAllowedPrimaryInactive, showDeletePrimaryInactive || showInactivatePrimaryInactive)}
                        </div>
                      </td>
                    </tr>
                  }
                  {showEditPrimaryInactive &&
                    <EditInsuranceCard
                      nikoPatientInsurancePreviewViewStore={nikoPatientInsurancePreviewViewStore}
                      keyPrefix="primaryInactive"
                    />
                  }
                  {showDeletePrimaryInactive &&
                    <DeleteInsuranceCard
                      nikoPatientInsurancePreviewViewStore={nikoPatientInsurancePreviewViewStore}
                      keyPrefix="primaryInactive"
                    />
                  }
                  {showInactivatePrimaryInactive &&
                    <InactivateInsuranceCard
                      nikoPatientInsurancePreviewViewStore={nikoPatientInsurancePreviewViewStore}
                      keyPrefix="primaryInactive"
                    />
                  }
                </tbody>
              </table>
              {!(patientInsurance.primaryActiveId && patientInsurance.primaryActiveInsurance.name === "Medicare") && !patientInsurance.primaryInactiveId &&
                <div>
                  <button className='btn btn--base btn--card btn--card--light' onClick={() => openAddCard("primaryInactive")} disabled={disabled}>
                    <i className="u-icon u-icon--sml u-icon--add-lined u-mar--right--xtny"></i>
                    Add Inactive Medicare Policy
                  </button>
                </div>
              }
              {showAddPrimaryInactive &&
                <InsuranceCard
                  nikoPatientInsurancePreviewViewStore={nikoPatientInsurancePreviewViewStore}
                  keyPrefix="primaryInactive"
                  action='Add'
                />
              }
            </div>
            <div className="card--sml card--outlined u-mar--bottom--sml--alt">
              <div className={` ${patientInsurance.secondaryActiveId ? 'u-mar--bottom--sml--alt' : 'u-mar--bottom--tny'}`}>
                <h3 className='modal__header__title u-mar--bottom--sml'>Secondary Insurance</h3>
                {!patientInsurance.secondaryActiveId &&
                  <BaasicButton
                    className="btn btn--sml btn--card btn--card--dark"
                    icon="u-icon u-icon--sml u-icon--add-lined u-mar--right--xtny"
                    label="Add Policy"
                    onClick={() => openAddCard("secondaryActive")}
                    disabled={disabled}
                  />
                }
              </div>
              <div>
                <table className="table w--100 type--color--primary">
                  <tbody>
                    {showAddSecondaryActive &&
                      <InsuranceCard
                        nikoPatientInsurancePreviewViewStore={nikoPatientInsurancePreviewViewStore}
                        keyPrefix="secondaryActive"
                        action="Add"
                      />
                    }
                    {patientInsurance.secondaryActiveId &&
                      <tr>
                        <td>{patientInsurance.secondaryActiveInsurance.name}</td>
                        <td className="type--right">{patientInsurance.secondaryActivePolicyNo}</td>
                        <td className="type--right">{toUTCDate(patientInsurance.secondaryActiveStartDate)}</td>
                        <td className="type--right">
                          <div className="insurance-action u-flex u-flex--default u-align--h--right u-flex--justify-end">
                            {renderActionButton("u-icon--reload--alt", "u-icon--reload--primary", showChangeSecondaryActive, () => openChangeCard("secondaryActive"))}
                            {renderActionButton("u-icon--edit-alt", "u-icon--edit-alt--primary", showEditSecondaryActive, () => openEditCard("secondaryActive"))}
                            {renderDeleteOrInactivate("secondaryActive", showDeleteAllowedSecondaryActive, showDeleteSecondaryActive || showInactivateSecondaryActive)}
                          </div>
                        </td>
                      </tr>
                    }
                    {showEditSecondaryActive &&
                      <EditInsuranceCard
                        nikoPatientInsurancePreviewViewStore={nikoPatientInsurancePreviewViewStore}
                        keyPrefix="secondaryActive"
                      />
                    }
                    {showDeleteSecondaryActive &&
                      <DeleteInsuranceCard
                        nikoPatientInsurancePreviewViewStore={nikoPatientInsurancePreviewViewStore}
                        keyPrefix="secondaryActive"
                      />
                    }
                    {showInactivateSecondaryActive &&
                      <InactivateInsuranceCard
                        nikoPatientInsurancePreviewViewStore={nikoPatientInsurancePreviewViewStore}
                        keyPrefix="secondaryActive"
                      />
                    }
                    {showChangeSecondaryActive &&
                      <InsuranceCard
                        nikoPatientInsurancePreviewViewStore={nikoPatientInsurancePreviewViewStore}
                        keyPrefix="secondaryActive"
                        action="Change"
                        attached
                      />
                    }
                    {(!patientInsurance.secondaryActiveId || !patientInsurance.secondaryActiveInsurance.name.includes("Medicaid")) && patientInsurance.secondaryInactiveId &&
                      <tr>
                        <td>
                          <span>{patientInsurance.secondaryInactiveInsurance.name}</span>
                          <div className="badge badge--tny badge--primary--light u-mar--left--xsml">Secondary inactive</div>
                        </td>
                        <td className="type--right">{patientInsurance.secondaryInactivePolicyNo}</td>
                        <td className="type--right">{toUTCDate(patientInsurance.secondaryInactiveStartDate)}</td>
                        <td className="type--right">
                          <div className="insurance-action u-flex u-flex--default u-align--h--right u-flex--justify-end">
                            {renderActionButton("u-icon--edit-alt", "u-icon--edit-alt--primary", showEditSecondaryInactive, () => openEditCard("secondaryInactive"))}
                            {renderDeleteOrInactivate("secondaryInactive", showDeleteAllowedSecondaryInactive, showDeleteSecondaryInactive || showInactivateSecondaryInactive)}
                          </div>
                        </td>
                      </tr>
                    }
                    {showEditSecondaryInactive &&
                      <EditInsuranceCard
                        nikoPatientInsurancePreviewViewStore={nikoPatientInsurancePreviewViewStore}
                        keyPrefix="secondaryInactive"
                      />
                    }
                    {showDeleteSecondaryInactive &&
                      <DeleteInsuranceCard
                        nikoPatientInsurancePreviewViewStore={nikoPatientInsurancePreviewViewStore}
                        keyPrefix="secondaryInactive"
                      />
                    }
                    {showInactivateSecondaryInactive &&
                      <InactivateInsuranceCard
                        nikoPatientInsurancePreviewViewStore={nikoPatientInsurancePreviewViewStore}
                        keyPrefix="secondaryInactive"
                      />
                    }
                  </tbody>
                </table>
              </div>
              {!(patientInsurance.secondaryActiveId && patientInsurance.secondaryActiveInsurance.name.includes("Medicaid")) && !patientInsurance.secondaryInactiveId &&
                <div>
                  <button className='btn btn--base btn--card btn--card--light' onClick={() => openAddCard("secondaryInactive")} disabled={disabled}>
                    <i className="u-icon u-icon--sml u-icon--add-lined u-mar--right--xtny"></i>
                    Add Inactive Medicaid Policy
                  </button>
                </div>
              }
              {showAddSecondaryInactive &&
                <InsuranceCard
                  nikoPatientInsurancePreviewViewStore={nikoPatientInsurancePreviewViewStore}
                  keyPrefix="secondaryInactive"
                  action="Add"
                />
              }
            </div>
          </div>
        </div>
      </Content>
      <BaasicConfirmModal modalParams={insuranceChangeConfirmModal} />
    </div>
  );
}

NikoPatientInsurancePreviewTemplate.propTypes = {
  nikoPatientInsurancePreviewViewStore: PropTypes.object
};

const InsuranceCard = observer(({ nikoPatientInsurancePreviewViewStore, keyPrefix, action, attached }) => {
  const content = (
    <div className={`row${attached ? '' : ' u-mar--y--sml'}`}>
      <NikoPatientInsuranceEdit
        pageViewStore={nikoPatientInsurancePreviewViewStore}
        keyPrefix={keyPrefix}
        action={action}
      />
    </div>
  );

  return (
    <tr>
      <td colSpan={"4"}>
        {attached ? <div className="insurance-action-panel">{content}</div> : content}
      </td>
    </tr>
  );
});

InsuranceCard.propTypes = {
  nikoPatientInsurancePreviewViewStore: PropTypes.object.isRequired,
  keyPrefix: PropTypes.string,
  action: PropTypes.string,
  attached: PropTypes.bool
};

const EditInsuranceCard = observer(({ nikoPatientInsurancePreviewViewStore, keyPrefix }) => {
  const {
    disabled,
    closeAll,
    showEditInsurancePolicy,
    confirmEditInsurancePolicy,
  } = nikoPatientInsurancePreviewViewStore;

  return (
    <tr>
      <td colSpan={"4"}>
        <div className="insurance-action-panel">
        {showEditInsurancePolicy ?
          <InsuranceCard
            nikoPatientInsurancePreviewViewStore={nikoPatientInsurancePreviewViewStore}
            keyPrefix={keyPrefix}
            action="Edit"
          />
          :
          <div>
            <h4 className='modal__header__title u-mar--bottom--sml'>You are about to edit this patient's active insurance</h4>
            <span>This action should be done only if the policy number of the current active insurance is incorrect. If the patient has a new insurance policy, please add it as a new one.</span>
            <div className="modal__footer">
              <BaasicButton disabled={disabled} className='btn btn--med btn--light' label="Cancel" onClick={() => closeAll()} />
              <BaasicButton disabled={disabled} className='btn btn--med btn--primary' label="Continue" onClick={() => confirmEditInsurancePolicy()} />
            </div>
          </div>
        }
        </div>
      </td>
    </tr>
  );
});

EditInsuranceCard.propTypes = {
  nikoPatientInsurancePreviewViewStore: PropTypes.object.isRequired,
  keyPrefix: PropTypes.string
};

const DeleteInsuranceCard = observer(({ nikoPatientInsurancePreviewViewStore, keyPrefix }) => {
  const {
    disabled,
    closeAll,
    deleteInsurancePolicy,
  } = nikoPatientInsurancePreviewViewStore;

  return (
    <tr>
      <td colSpan={"4"}>
        <div className="insurance-action-panel insurance-action-panel--flush">
          <h4 className='modal__header__title u-mar--bottom--sml'>Are you sure you want to Delete this Insurance Policy?</h4>
          <span>This action should only be done if the policy was added in error. If there is a new policy that is replacing this one, exit this screen and click on the change icon to add the new policy.</span>
          <div className="modal__footer">
            <BaasicButton disabled={disabled} className='btn btn--med btn--light' label="Cancel" onClick={() => closeAll()} />
            <BaasicButton disabled={disabled} icon={disabled ? "u-icon u-icon--xmed u-icon--sync u-rotate--login" : 'u-display--none'} className='btn btn--med btn--primary' label="Confirm" onClick={() => deleteInsurancePolicy(keyPrefix)} />
          </div>
        </div>
      </td>
    </tr>
  );
});

DeleteInsuranceCard.propTypes = {
  nikoPatientInsurancePreviewViewStore: PropTypes.object.isRequired,
  keyPrefix: PropTypes.string
};

@observer
class InactivateInsuranceCard extends React.Component {
  constructor(props) {
    super(props);
    const existingEndDate = props.nikoPatientInsurancePreviewViewStore.patientInsurance[`${props.keyPrefix}EndDate`];
    this.state = {
      endDate: existingEndDate ? new Date(existingEndDate) : null,
      endDateUnavailable: false,
      endDateError: null,
    };
  }

  validate = () => {
    const { endDate, endDateUnavailable } = this.state;
    const { keyPrefix, nikoPatientInsurancePreviewViewStore } = this.props;

    if (!endDateUnavailable && !endDate) {
      return "The End Date field is required.";
    }
    const startDate = nikoPatientInsurancePreviewViewStore.patientInsurance[`${keyPrefix}StartDate`];
    if (!endDateUnavailable && startDate && endDate && new Date(endDate) <= new Date(startDate)) {
      return "End Date must be greater than Start Date.";
    }
    return null;
  };

  onConfirm = () => {
    const { keyPrefix, nikoPatientInsurancePreviewViewStore } = this.props;
    const { endDate, endDateUnavailable } = this.state;

    const error = this.validate();
    this.setState({ endDateError: error });
    if (error) return;

    nikoPatientInsurancePreviewViewStore.inactivateInsurancePolicy(keyPrefix, endDate, endDateUnavailable);
  };

  render() {
    const { nikoPatientInsurancePreviewViewStore, keyPrefix } = this.props;
    const { disabled, closeAll } = nikoPatientInsurancePreviewViewStore;
    const { endDate, endDateUnavailable, endDateError } = this.state;

    return (
      <tr>
        <td colSpan={"4"}>
          <div className="insurance-action-panel insurance-action-panel--flush">
            <h4 className='modal__header__title u-mar--bottom--sml'>Are you sure you want to Inactivate this Insurance Policy?</h4>
            <span>This action should only be done if the policy was added in error. If there is a new policy that is replacing this one, exit this screen and click on the change icon to add the new policy.</span>
            <div className="row u-flex--align-end u-mar--top--sml">
              <div className="form__group col col-lrg-6 u-mar--bottom--sml">
                <label className="form__group__label">
                  End Date
                  {!endDateUnavailable && <span className="validation__mark">*</span>}
                </label>
                <DatePicker
                  value={endDate}
                  onChange={(val) => this.setState({ endDate: val, endDateError: null })}
                  disabled={endDateUnavailable || disabled}
                />
                {endDateError &&
                  <div className="validation__message">
                    <i className="u-icon u-icon--xsml u-icon--warning u-mar--right--tny"></i>{endDateError}
                  </div>
                }
              </div>
              <div className="form__group col col-lrg-6 u-mar--bottom--med-lrg">
                <label className="form__group__label--checkbox">
                  <input
                    type="checkbox"
                    id={`endDateUnavailable-${keyPrefix}`}
                    className="input--check"
                    checked={endDateUnavailable}
                    onChange={(e) => this.setState({
                      endDateUnavailable: e.target.checked,
                      endDate: e.target.checked ? null : endDate,
                      endDateError: null,
                    })}
                    disabled={disabled}
                  />
                  <label htmlFor={`endDateUnavailable-${keyPrefix}`}>End Date Unavailable</label>
                </label>
              </div>
            </div>
            <div className="modal__footer">
              <BaasicButton disabled={disabled} className='btn btn--med btn--light' label="Cancel" onClick={() => closeAll()} />
              <BaasicButton disabled={disabled} icon={disabled ? "u-icon u-icon--xmed u-icon--sync u-rotate--login" : 'u-display--none'} className='btn btn--med btn--primary' label="Confirm" onClick={this.onConfirm} />
            </div>
          </div>
        </td>
      </tr>
    );
  }
}

InactivateInsuranceCard.propTypes = {
  nikoPatientInsurancePreviewViewStore: PropTypes.object.isRequired,
  keyPrefix: PropTypes.string
};

export default defaultTemplate(NikoPatientInsurancePreviewTemplate);